import { AxiosError } from 'axios';
import { apiClient } from './api';
import {
  ConflictStatus,
  ConflictType,
  ICalConflict,
  IcalConflictActionError,
  IcalConflictActionErrorCode,
  ResolutionOutcome,
  ResolveConflictRequest,
  StoredResolutionOutcome,
} from '../types/icalConflict';

/**
 * NOT YET LIVE — see specs/037-booking-conflict-resolution/contracts/ical-conflicts-api.md.
 * This module is the single isolation point for the not-yet-live `ical-conflicts` endpoints
 * (research.md Decision 1): swapping to the real network calls, once backend spec 053 ships,
 * requires editing only this file — no signature changes for callers (hooks/components).
 */

/**
 * Maps the frontend REQUEST-vocabulary outcome (`CANCEL_PLATFORM_BOOKING` | `DISMISS`) to the
 * backend's STORED-vocabulary outcome (`PLATFORM_BOOKING_CANCELLED` | `DISMISSED`). These are two
 * different enums for the same decision — request intent vs. stored historical outcome — and must
 * never be treated as the same string on the wire (see icalConflict.ts doc comments).
 */
export const mapRequestOutcomeToStoredOutcome = (outcome: ResolutionOutcome): StoredResolutionOutcome => {
  switch (outcome) {
    case 'CANCEL_PLATFORM_BOOKING':
      return 'PLATFORM_BOOKING_CANCELLED';
    case 'DISMISS':
      return 'DISMISSED';
  }
};

/** Inverse of `mapRequestOutcomeToStoredOutcome` — used when parsing a resolved conflict from the wire. */
export const mapStoredOutcomeToRequestOutcome = (outcome: StoredResolutionOutcome): ResolutionOutcome => {
  switch (outcome) {
    case 'PLATFORM_BOOKING_CANCELLED':
      return 'CANCEL_PLATFORM_BOOKING';
    case 'DISMISSED':
      return 'DISMISS';
  }
};

/** Maps a resolve-conflict Axios error into a stable, owner-facing error code (404/409/403). */
export const mapIcalConflictActionError = (err: unknown): IcalConflictActionError => {
  const status = (err as AxiosError)?.response?.status;

  if (status === 404) {
    return { code: 'NOT_FOUND', cause: err };
  }
  if (status === 409) {
    return { code: 'ALREADY_RESOLVED', cause: err };
  }
  if (status === 403) {
    return { code: 'FORBIDDEN', cause: err };
  }

  return { code: 'UNKNOWN', cause: err };
};

interface ApiICalConflictResponse {
  readonly conflictId?: string;
  readonly apartmentId?: string;
  readonly feedId?: string;
  readonly feedSourceLabel?: string;
  readonly icalUid?: string;
  readonly startDate?: string;
  readonly endDate?: string;
  readonly conflictType?: ConflictType;
  readonly linkedBookingId?: string | null;
  readonly status?: ConflictStatus;
  readonly detectedAt?: string;
  /** Wire value uses the backend's STORED vocabulary, not the request vocabulary. */
  readonly resolutionOutcome?: StoredResolutionOutcome | null;
  readonly resolvedByUserId?: string | null;
  readonly resolvedAt?: string | null;
}

const mapConflict = (conflict: ApiICalConflictResponse): ICalConflict => ({
  conflictId: conflict.conflictId ?? '',
  apartmentId: conflict.apartmentId ?? '',
  feedId: conflict.feedId ?? '',
  feedSourceLabel: conflict.feedSourceLabel ?? '',
  icalUid: conflict.icalUid,
  startDate: conflict.startDate ?? '',
  endDate: conflict.endDate ?? '',
  conflictType: conflict.conflictType ?? 'DOUBLE_BOOKING',
  linkedBookingId: conflict.linkedBookingId ?? undefined,
  status: conflict.status ?? 'ACTIVE',
  detectedAt: conflict.detectedAt ?? '',
  resolutionOutcome: conflict.resolutionOutcome
    ? mapStoredOutcomeToRequestOutcome(conflict.resolutionOutcome)
    : undefined,
  resolvedByUserId: conflict.resolvedByUserId ?? undefined,
  resolvedAt: conflict.resolvedAt ?? undefined,
});

export const icalConflictService = {
  /**
   * GET /apartments/{apartmentId}/ical-conflicts
   * Lists conflicts for the apartment, optionally scoped to `status`.
   */
  async listConflicts(apartmentId: string, status?: ConflictStatus): Promise<ICalConflict[]> {
    const response = await apiClient.get<ApiICalConflictResponse[]>(
      `/apartments/${apartmentId}/ical-conflicts`,
      { params: { status } }
    );
    return response.data.map(mapConflict);
  },

  /**
   * POST /apartments/{apartmentId}/ical-conflicts/{conflictId}/resolve
   * Resolves an active DOUBLE_BOOKING conflict with the given (request-vocabulary) outcome.
   */
  async resolveConflict(
    apartmentId: string,
    conflictId: string,
    request: ResolveConflictRequest
  ): Promise<ICalConflict> {
    try {
      const response = await apiClient.post<ApiICalConflictResponse>(
        `/apartments/${apartmentId}/ical-conflicts/${conflictId}/resolve`,
        request
      );
      return mapConflict(response.data);
    } catch (err) {
      throw mapIcalConflictActionError(err);
    }
  },
};

export type { IcalConflictActionErrorCode };
